import { isDebug } from './App.config'
import { SessionInfo } from './Types'
import { build, commitID, environmentName } from './App.config'
import platform from 'platform'
import { useState } from 'react'

export const sapphireConsoleCSS = 'padding: 4px 6px; background-color: #f5f5f5; color: #003462; border-left: 3px solid #f47e56'
export const addBodyClass = (className: string) => document.body.classList.add(className)
export const removeBodyClass = (className: string) => document.body.classList.remove(className)

export const isEmpty = function(obj: object): boolean {
    if (Object.entries(obj).length === 0 && obj.constructor === Object) return true
    return false
}

export const sumProp = (array: any[], profileProperty: string): number => array.reduce((prev, cur) => prev + cur[`${profileProperty}`], 0)

const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)

export const getGuid = () => (S4() + S4() + '-' + S4() + '-4' + S4().substr(0, 3) + '-' + S4() + '-' + S4() + S4() + S4()).toLowerCase()

export const isString = (value: any) => typeof value === 'string' || value instanceof String

export const toBoolean = (value: string | number | boolean): boolean => {
    if (typeof value === 'string') value = value.toLowerCase()
    return [true, 'true', '1', 1].includes(value)
}

export const openInNewTab = (href: string) => window.open(href, '_blank')

export const simulateNetworkRequest = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds))

export const preventContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => !isDebug && e.preventDefault()

export const retrieveParamByName = (name: string, url: string) => {
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export enum StorageType {
    LocalStorage = 'localStorage',
    SessionStorage = 'sessionStorage'
}

export const storageAvailable = (type: StorageType): boolean => {
    try {
        const storage = window[type]
        const x = '__storage_test__'
        storage.setItem(x, x)
        storage.removeItem(x)
        return true
    }
    catch (e) {
        return false
    }
}

export const storeInSession = (key: string, value: string) => {
    if (storageAvailable(StorageType.SessionStorage)) {
        window.sessionStorage.setItem(key, value)
        isDebug && console.log(`%cSession storage successful! (${key}, ${value})`, sapphireConsoleCSS)
    }
    else {
        throw new Error(`Failed to store (${key}, ${value}) in session storage. Error: Session storage is unavailable!`)
    }
}

export const getFromSession = (key: string): string => {
    isDebug && console.log(`%cRetrieving key '${key}' from session...`, sapphireConsoleCSS)

    let value: string = ''
    if (storageAvailable(StorageType.SessionStorage)) {
        if (window.sessionStorage.getItem(key)) {
            value = window.sessionStorage.getItem(key)!
            isDebug && console.log(`%cGet session key '${key}' successful! Value: ${value}`, sapphireConsoleCSS)
        }
    }
    else {
        throw new Error(`%cFailed to get session key '${key}'! Error: Session storage is unavailable!`)
    }
    return value
}

export const removeFromSession = (key: string) => {
    isDebug && console.log(`%cRemoving key '${key}' from session...`, sapphireConsoleCSS)
    window.sessionStorage.removeItem(key)
}

export const getSessionInfo = (): SessionInfo => {
    const navUserAgent = navigator.userAgent
    const screenResolution = `${window.screen.width}x${window.screen.height}`
    const windowDimensions = `${window.innerWidth}x${window.innerHeight}`

    const sessionInfo: SessionInfo = {
        environment: environmentName,
        'build-number': build,
        'commit-number': commitID,
        'user-agent': navUserAgent,
        browser: platform.name,
        'browser-version': platform.version,
        os: platform.os.family,
        version: platform.os.version,
        'screen-size': screenResolution,
        'browser-window-size': windowDimensions
    }

    return sessionInfo
}

export const useLocalStorage = <T extends unknown>(key: string, initialValue: T) => {
    const isServer = typeof window === 'undefined'

    const [storedValue, setStoredValue] = useState(() => {
        if (isServer) return initialValue

        try {
            const item = window.localStorage.getItem(key)
            return item ? JSON.parse(item) : initialValue
        }
        catch (error) {
            console.log(error)
            return initialValue
        }
    })

    const setValue = (value: T) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value
            setStoredValue(valueToStore)
            if (!isServer) {
                window.localStorage.setItem(key, JSON.stringify(valueToStore))
            }
        }
        catch (error) {

            console.log(error)
        }
    }

    return [storedValue, setValue]
}
